import { stopActiveMediaStream } from '@/components/shared/mediaStreamManager';
import ROUTES from '@/routes/path';
import { detectMobile, isIOS, LayoutVisibilityContext } from '@/utils/common-utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const LayoutVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showFooter, setShowFooter] = useState(false);
  const [showFooterLifeX, setShowFooterLifeX] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [hasIntercom, setHasIntercom] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();

  const _isIOS = isIOS();
  const _isMobile = detectMobile();

  const setAppHeight = useCallback(() => {
    document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
  }, []);

  useEffect(() => {
    let previousViewportHeight = window.visualViewport?.height || window.innerHeight;
    let isKeyboardOpen = false;
    const menuAllowedPaths = ['intro', 'chooseflow', 'likeness', 'interview', 'guide'];

    const handleLandscape = () => {
      if (window.screen?.orientation) {
        setIsLandscape(window.screen.orientation.type.startsWith('landscape'));
      } else {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    const handleVisualViewportResize = () => {
      const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
      if (!isKeyboardOpen && currentViewportHeight < previousViewportHeight - 100) {
        isKeyboardOpen = true;
      }
      if (isKeyboardOpen && currentViewportHeight >= window.innerHeight - 100) {
        isKeyboardOpen = false;
        setAppHeight();
        window.scrollTo(0, 0);
      }
      previousViewportHeight = currentViewportHeight;
    };

    const handleResizeOrOrientation = () => {
      setIsMobile(detectMobile());
      handleLandscape();
      setTimeout(() => {
        setAppHeight();
        window.scrollTo(0, 0);
      }, 100);
    };

    const stopMediaStreams = () => {
      const mediaElementsPage = [ROUTES.twinPermission, ROUTES.twinLikeness, ROUTES.twinCameraCheck];
      const currentPath = window.location.pathname;

      const shouldKeepMedia = mediaElementsPage.some(keyword => currentPath.includes(keyword));
      if (!shouldKeepMedia) {
        stopActiveMediaStream();
      }
    };
    stopMediaStreams();

    setAppHeight();
    setIsMobile(_isMobile);
    if (location.pathname && menuAllowedPaths.some(path => location.pathname.includes(path))) {
      setShowMenu(_isMobile);
    } else {
      setShowMenu(false);
    }
    setShowFooter(!_isMobile);
    handleLandscape();

    if (_isIOS && window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
    } else {
      window.addEventListener('resize', handleResizeOrOrientation);
    }
    window.addEventListener('orientationchange', handleResizeOrOrientation);
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault();
    });

    return () => {
      if (_isIOS && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      } else {
        window.removeEventListener('resize', handleResizeOrOrientation);
      }
      window.removeEventListener('orientationchange', handleResizeOrOrientation);
    };
  }, [_isIOS, setAppHeight, _isMobile, location.pathname]);

  return (
    <LayoutVisibilityContext.Provider
      value={{
        showFooter,
        setShowFooter,
        showFooterLifeX,
        setShowFooterLifeX,
        showHeader,
        setShowHeader,
        isMobile,
        isLandscape,
        hasIntercom,
        setHasIntercom,
        _isIOS,
        setAppHeight,
        setShowMenu,
        showMenu,
      }}
    >
      {children}
    </LayoutVisibilityContext.Provider>
  );
};
